/**
 * Account Modal - Happy Angler Login & Club Registration
 * Modal ladataan DOM:iin vasta kun avataan ensimmäisen kerran (lazy instantiation)
 * Turnstile-suojattu versio
 * Version: 1.2
 *
 * Changes in 1.1:
 * - PERFORMANCE: Replaced setTimeout polling with requestAnimationFrame + timeout
 *
 * Changes in 1.2:
 * - FIX: Submit button now requires marketing consent checkbox in addition to Turnstile
 * - FIX: Submit state re-evaluated on every input/change event in the form
 */

(function() {
  'use strict';

  // Configuration
  const WORKER_URL = 'https://ha-loyalty-worker.hagrowly.workers.dev';
  const FETCH_TIMEOUT = 10000;

  // Template element - modal ei ole DOM:ssa ennen avaamista
  const template = document.getElementById('account-modal-template');
  if (!template) return;

  // State
  let modal = null;
  let views = {};
  let currentView = 'login';
  let isInitialized = false;
  let lastActiveElement = null;
  let scrollPosition = 0;
  let turnstileToken = null;
  let turnstileWidgetId = null;
  let turnstileLoadTriggered = false;

  // ========== Turnstile Callbacks (Global) ==========

  window.onAccountTurnstileSuccess = function(token) {
    turnstileToken = token;
    updateSubmitState();
  };

  window.onAccountTurnstileError = function() {
    turnstileToken = null;
    updateSubmitState();
    const settings = window.accountModalSettings?.errors || {};
    showError(settings.turnstile || 'Vahvistus epäonnistui, yritä uudelleen');
  };

  window.onAccountTurnstileExpired = function() {
    turnstileToken = null;
    updateSubmitState();
  };

  // ========== Submit State Validation ==========

  // Tarkistaa että pakolliset kentät täytetty + marketing hyväksytty + turnstile ok
  function updateSubmitState() {
    const form = modal?.querySelector('#loyalty-register-form');
    const submitBtn = form?.querySelector('.account-modal__btn--primary');
    if (!submitBtn) return;

    const required = window.accountModalSettings?.required || { email: true, phone: true };

    const emailVal = form.querySelector('[name="email"]')?.value?.trim() || '';
    const phoneVal = form.querySelector('[name="phone"]')?.value?.trim() || '';

    const emailOk = !required.email || isValidEmail(emailVal);
    const phoneOk = !required.phone || !!phoneVal;
    const marketingOk = form.querySelector('[name="marketingConsent"]')?.checked || false;
    const turnstileOk = !!turnstileToken;

    submitBtn.disabled = !(emailOk && phoneOk && marketingOk && turnstileOk);
  }

  // ========== Lazy Initialization ==========

  function initModal() {
    if (isInitialized) return;
    
    // Clone template content and append to body
    const content = template.content.cloneNode(true);
    document.body.appendChild(content);
    
    // Get references
    modal = document.getElementById('account-modal');
    
    views = {
      login: modal.querySelector('[data-view="login"]'),
      register: modal.querySelector('[data-view="register"]'),
      success: modal.querySelector('[data-view="success"]')
    };
    
    // Setup event listeners
    setupEventListeners();
    
    isInitialized = true;
  }

  function setupEventListeners() {
    // Close modal
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-account-modal-close]')) {
        closeModal();
      }
    });

    // View switching
    modal.addEventListener('click', (e) => {
      const switcher = e.target.closest('[data-switch-view]');
      if (switcher) {
        e.preventDefault();
        const targetView = switcher.dataset.switchView;
        switchView(targetView);
      }
    });

    // Register form submit - käytetään event delegatiota
    modal.addEventListener('submit', (e) => {
      const form = e.target.closest('#loyalty-register-form');
      if (form) {
        e.preventDefault();
        handleRegisterSubmit(e);
      }
    });

    // Validoi lomake aina kun kenttä muuttuu
    modal.addEventListener('input', updateSubmitState);
    modal.addEventListener('change', updateSubmitState);

    // Turnstile lazy-trigger: lataa vasta kun käyttäjä alkaa täyttää lomaketta
    const triggerTurnstileFromForm = (e) => {
      if (turnstileLoadTriggered) return;
      if (!e.target.closest('#loyalty-register-form')) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;
      turnstileLoadTriggered = true;
      renderTurnstile();
    };
    modal.addEventListener('focusin', triggerTurnstileFromForm);
    modal.addEventListener('input', triggerTurnstileFromForm);
  }

  // ========== Turnstile Rendering ==========

  // Lataa Turnstile API lazy:nä — vain kun modaali avataan.
  // Skripti ei ole enää theme.liquid:ssa → ei SEO-herjoja sisältösivuilla.
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  function ensureTurnstileScript() {
    if (window.turnstile) return;
    if (document.querySelector('script[data-turnstile-loader]')) return;
    const s = document.createElement('script');
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.setAttribute('data-turnstile-loader', '');
    document.head.appendChild(s);
  }

  // Odota Turnstile API:n latautumista (max 5s)
  function waitForTurnstile(maxWait = 5000) {
    ensureTurnstileScript();
    return new Promise((resolve) => {
      if (window.turnstile) {
        resolve(true);
        return;
      }
      const start = Date.now();
      const check = () => {
        if (window.turnstile) {
          resolve(true);
        } else if (Date.now() - start > maxWait) {
          resolve(false);
        } else {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    });
  }

  async function renderTurnstile() {
    // Odota Turnstile API:n latautumista
    const available = await waitForTurnstile();
    if (!available) return;

    const container = modal?.querySelector('#account-turnstile');
    if (!container) return;

    // Jos widget on jo renderöity, reset se
    if (turnstileWidgetId !== null) {
      try {
        turnstile.reset(turnstileWidgetId);
      } catch (e) {
        // Widget ei ollut enää olemassa, renderöi uusi
        turnstileWidgetId = null;
      }
    }

    // Renderöi uusi widget jos ei ole
    if (turnstileWidgetId === null) {
      const siteKey = container.dataset.sitekey;
      if (!siteKey) return;

      turnstileWidgetId = turnstile.render(container, {
        sitekey: siteKey,
        callback: window.onAccountTurnstileSuccess,
        'error-callback': window.onAccountTurnstileError,
        'expired-callback': window.onAccountTurnstileExpired,
        theme: 'light',
        // 'invisible' is NOT a valid size for turnstile.render() — it throws and the
        // widget never issues a token, leaving the submit button permanently disabled.
        // Valid sizes: 'compact' | 'flexible' | 'normal'. Use 'flexible' (matches the
        // markup's data-size); 'interaction-only' keeps it unobtrusive (hidden unless
        // a challenge is actually required) while still issuing a token.
        size: 'flexible',
        appearance: 'interaction-only'
      });
    }
  }

  function resetTurnstile() {
    turnstileToken = null;
    if (turnstileWidgetId !== null && window.turnstile) {
      try {
        turnstile.reset(turnstileWidgetId);
      } catch (e) {
        // Ignore
      }
    }
    updateSubmitState();
  }

  // ========== Modal Controls ==========

  function openModal(initialView = 'login') {
    // Initialize on first open
    initModal();
    
    // Prevent duplicate opens
    if (modal.getAttribute('aria-hidden') === 'false') return;
    
    // Store last focused element for restoration
    lastActiveElement = document.activeElement;
    
    // iOS-compatible scroll lock
    scrollPosition = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%';
    
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('account-modal-open');
    
    // Show requested view
    switchView(initialView);
    
    // Focus first input after animation
    setTimeout(() => {
      const currentViewEl = views[currentView];
      const firstInput = currentViewEl?.querySelector('input');
      if (firstInput) firstInput.focus();
    }, 100);

    // Keyboard handlers
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', trapFocus);
  }

  function closeModal() {
    if (!modal) return;
    
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('account-modal-open');
    
    // Restore scroll position (iOS-compatible)
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollPosition);
    
    // Remove keyboard handlers
    document.removeEventListener('keydown', handleEscape);
    document.removeEventListener('keydown', trapFocus);
    
    // Restore focus to trigger element
    if (lastActiveElement?.focus) {
      lastActiveElement.focus();
    }
    
    // Reset after animation
    setTimeout(() => {
      resetModal();
    }, 300);
  }

  function handleEscape(e) {
    if (e.key === 'Escape') closeModal();
  }

  // Focus trap - keep focus inside current view
  function trapFocus(e) {
    if (e.key !== 'Tab' || !modal) return;
    
    // Get focusables only from current visible view
    const currentViewEl = views[currentView];
    if (!currentViewEl) return;
    
    const focusables = currentViewEl.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    // Include close button (outside views)
    const closeBtn = modal.querySelector('.account-modal__close');
    const allFocusables = closeBtn ? [closeBtn, ...focusables] : [...focusables];
    
    if (!allFocusables.length) return;
    
    const first = allFocusables[0];
    const last = allFocusables[allFocusables.length - 1];
    
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function resetModal() {
    // Reset form
    const form = modal?.querySelector('#loyalty-register-form');
    if (form) form.reset();

    // Hide error
    const error = modal?.querySelector('#loyalty-error');
    if (error) error.style.display = 'none';

    // Reset Turnstile
    resetTurnstile();
    turnstileLoadTriggered = false;

    // Reset to login view
    switchView('login');
  }

  // ========== View Switching ==========

  function switchView(viewName, keepError = false) {
    if (!views[viewName]) return;
    
    // Hide all views
    Object.keys(views).forEach(key => {
      if (views[key]) {
        views[key].style.display = 'none';
      }
    });
    
    // Show requested view
    views[viewName].style.display = '';
    currentView = viewName;
    
    // Clear errors only when leaving register view (unless keepError is true)
    if (!keepError && viewName !== 'register') {
      const error = modal?.querySelector('#loyalty-error');
      if (error) error.style.display = 'none';
    }
    
    // Turnstile latautuu vasta kun käyttäjä alkaa täyttää kenttiä
    // (ks. setupEventListeners → triggerTurnstileFromForm).
    if (viewName === 'register') {
      // Evaluoi submit-tila heti kun näkymä vaihdetaan
      setTimeout(updateSubmitState, 60);
    }
    
    // Focus first input
    setTimeout(() => {
      const firstInput = views[viewName]?.querySelector('input');
      if (firstInput) firstInput.focus();
    }, 50);
  }

  // ========== Form Submission (Registration) ==========

  async function handleRegisterSubmit(e) {
    e.preventDefault();
    
    const form = e.target.closest('#loyalty-register-form') || e.target;
    if (!form) return;

    const submitBtn = form.querySelector('.account-modal__btn--primary');
    const submitText = submitBtn?.querySelector('.account-modal__submit-text');
    const submitLoading = submitBtn?.querySelector('.account-modal__submit-loading');

    // Check Turnstile token
    if (!turnstileToken) {
      const settings = window.accountModalSettings?.errors || {};
      showError(settings.turnstile || 'Odota vahvistuksen latautumista');
      return;
    }

    // Get form data
    const fullName = form.querySelector('[name="name"]')?.value?.trim() || '';
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const countryCode = form.querySelector('[name="countryCode"]')?.value || '';

    const data = {
      email: form.querySelector('[name="email"]')?.value?.trim() || '',
      firstName: firstName,
      lastName: lastName,
      phone: form.querySelector('[name="phone"]')?.value?.trim() || '',
      marketingConsent: form.querySelector('[name="marketingConsent"]')?.checked || false,
      countryCode: countryCode,
      turnstileToken: turnstileToken
    };

    // Get required settings
    const required = window.accountModalSettings?.required || { email: true, phone: true };
    const errorMessages = window.accountModalSettings?.errors || {};

    // Validate email (if required or if provided)
    if (required.email && (!data.email || !isValidEmail(data.email))) {
      showError(errorMessages.email_invalid || 'Tarkista sähköpostiosoite');
      return;
    }
    
    // Validate phone (if required)
    if (required.phone && !data.phone) {
      showError(errorMessages.phone_invalid || 'Puhelinnumero on pakollinen');
      return;
    }

    // Show loading state
    if (submitBtn) submitBtn.disabled = true;
    if (submitText) submitText.style.display = 'none';
    if (submitLoading) submitLoading.style.display = '';
    
    // Hide previous error
    const errorBefore = modal?.querySelector('#loyalty-error');
    if (errorBefore) errorBefore.style.display = 'none';

    // Fetch with timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data),
        signal: controller.signal
      });
      
      clearTimeout(timeout);

      // Try to parse JSON, fallback to null
      let result = null;
      try {
        result = await response.json();
      } catch (_) {
        // JSON parse failed
      }

      // Check response status
      if (!response.ok || !result) {
        const settings = window.accountModalSettings?.errors || {};
        showError(result?.errors ? parseError(result.errors) : (settings.connection || 'Yhteysvirhe, yritä uudelleen'));
        resetTurnstile();
        return;
      }

      if (result.success) {
        // Show success view
        switchView('success');
      } else {
        // Handle errors - stay on register view and show error
        const errorMessage = parseError(result.errors);
        showError(errorMessage);
        resetTurnstile();
      }
    } catch (error) {
      clearTimeout(timeout);
      const settings = window.accountModalSettings?.errors || {};
      // Check if aborted (timeout)
      if (error.name === 'AbortError') {
        showError(settings.connection || 'Pyyntö aikakatkaistiin, yritä uudelleen');
      } else {
        showError(settings.connection || 'Yhteysvirhe, yritä hetken kuluttua uudelleen');
      }
      resetTurnstile();
    } finally {
      // Palauta napin tila updateSubmitState-funktion kautta
      if (submitText) submitText.style.display = '';
      if (submitLoading) submitLoading.style.display = 'none';
      updateSubmitState();
    }
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function showError(message) {
    // Haetaan error-elementti dynaamisesti joka kerta
    const error = modal?.querySelector('#loyalty-error');
    if (!error) return;
    const errorText = error.querySelector('.account-modal__error-text');
    if (errorText) errorText.textContent = message;
    error.style.display = '';
  }

  function parseError(errors) {
    const settings = window.accountModalSettings?.errors || {};
    
    if (!errors || !errors.length) return settings.generic || 'Rekisteröinti epäonnistui';

    const error = errors[0];
    
    // Custom error codes from Worker (check both message and code)
    const errorCode = (error.message || error.code || '').toLowerCase();
    
    if (errorCode === 'already_member') {
      return settings.already_member || 'Olet jo Happy Angler Clubin jäsen';
    }
    
    if (errorCode === 'phone_taken' || (errorCode.includes('phone') && errorCode.includes('taken'))) {
      return settings.phone_taken || 'Tämä puhelinnumero on jo rekisteröity';
    }
    
    if (errorCode === 'turnstile_failed' || errorCode === 'missing_turnstile_token') {
      return settings.turnstile || 'Vahvistus epäonnistui, yritä uudelleen';
    }
    
    if (errorCode === 'rate_limit_exceeded') {
      return 'Liian monta yritystä, odota hetki';
    }
    
    if (errorCode === 'invalid_origin') {
      return settings.generic || 'Rekisteröinti epäonnistui';
    }
    
    // Common Shopify customer errors
    if (error.field?.includes('email')) {
      if (error.message?.includes('taken')) {
        return settings.email_taken || 'Tämä sähköpostiosoite on jo rekisteröity';
      }
      return settings.email_invalid || 'Tarkista sähköpostiosoite';
    }
    
    if (error.field?.includes('phone')) {
      if (error.message?.includes('taken')) {
        return settings.phone_taken || 'Tämä puhelinnumero on jo rekisteröity';
      }
      return settings.phone_invalid || 'Tarkista puhelinnumero';
    }

    return settings.generic || error.message || 'Rekisteröinti epäonnistui';
  }

  // ========== Global Event Listeners (before modal init) ==========

  // Open modal from triggers (combined listener)
  document.addEventListener('click', (e) => {
    // data-account-modal-open attribute
    const trigger = e.target.closest('[data-account-modal-open]');
    if (trigger) {
      // Tarkista onko käyttäjä kirjautunut (Shopify asettaa tämän)
      if (window.Shopify?.customer) {
        // Kirjautunut - ohjaa account-sivulle, älä avaa modaalia
        window.location.href = '/account';
        return;
      }
      
      e.preventDefault();
      const view = trigger.dataset.accountModalOpen || 'login';
      openModal(view);
      return;
    }
    
    // #account hash link
    const accountLink = e.target.closest('a[href="#account"]');
    if (accountLink) {
      if (window.Shopify?.customer) {
        window.location.href = '/account';
        return;
      }
      e.preventDefault();
      openModal('login');
    }
  });

  // Check URL hash on page load (delayed to not block render)
  if (window.location.hash === '#account') {
    requestAnimationFrame(() => {
      openModal('login');
    });
  }

  // ========== Global API ==========

  window.accountModal = {
    open: openModal,
    close: closeModal,
    switchView: (view) => switchView(view)
  };

})();